import { useTheme } from './ThemeProvider';

/**
 * The KaziPeople palette for inline-styled components.
 *
 * ⚠️ THESE ARE REFERENCES, NOT VALUES. Every entry is a `var(--token)` pointing
 * at src/styles/index.css, which is the one place colours are defined.
 *
 * It used to restate them as hex, light and dark, and the two had drifted:
 * dark surfaces here were #111318 / #1C2030 / #161A24 while the tokens said
 * #16181F / #1E212B, and dark body text was #EFF1F8 against the token's
 * #ECE7DF. Two panels side by side could be different greys depending on which
 * file the component had imported. A copy of a palette is a palette that will
 * drift; the fix is to stop copying.
 *
 * ⚠️ `dark` is now IGNORED, deliberately. A CSS variable already resolves to the
 * right value for the active theme, so branching in JS would re-introduce the
 * second opinion this change exists to remove. The parameter stays so the 9
 * call sites keep working, and so does useDark(), which several of them use for
 * non-colour decisions.
 *
 * The consequence to know about: components using this now render the SAME dark
 * surfaces as the rest of the app. That is the point, and it is a visible change
 * in dark mode for the time-off and employee screens.
 */
export function makeC(_dark?: boolean) {
  return {
    // Text
    charcoal: 'var(--foreground)',
    charcoalMid: 'var(--text-mid)',
    charcoalLight: 'var(--muted-foreground)',
    charcoalSubtle: 'var(--text-subtle)',
    // Brand
    orange: 'var(--color-primary)',
    orangeDark: 'var(--color-primary-dark)',
    orangeLight: 'var(--accent)',
    peach: 'var(--color-primary-light)',
    // Surfaces
    cream: 'var(--background)',
    creamDark: 'var(--surface-sunken)',
    creamDeep: 'var(--surface-deep)',
    white: 'var(--card)',
    sidebarBg: 'var(--sidebar)',
    // Lines
    border: 'var(--token-border)',
    borderMed: 'var(--token-border-strong)',
    // Status. Soft is a background, strong is text on it — never the reverse.
    green: 'var(--color-success)',
    greenLight: 'var(--color-success-soft)',
    greenText: 'var(--color-success-strong)',
    red: 'var(--color-danger)',
    redLight: 'var(--color-danger-soft)',
    redText: 'var(--color-danger-strong)',
    yellow: 'var(--color-warning)',
    yellowLight: 'var(--color-warning-soft)',
    yellowText: 'var(--color-warning-strong)',
    blue: 'var(--color-info)',
    blueLight: 'var(--color-info-soft)',
    blueText: 'var(--color-info-strong)',
  };
}

export type C = ReturnType<typeof makeC>;

export const useDark = (): boolean => useTheme().theme === 'dark';

/** The palette. Stable across renders: it holds no theme-dependent values. */
const PALETTE = makeC();
export const useC = (): C => PALETTE;
